import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  GlobalTable,
  HeaderLine,
  Td,
  TdButtons,
  Th,
  Tr,
} from '../../../../components/styles/table-style'
import { ConfigMenuButton, ConfigMenuWrapper } from '../../SteelOrders/SteelOrdersTable/style'
import axios from '../../../../config/axios'
import lookup from '../../../../assets/certificates/Tables/lookup.svg'
import { useNavigate, useOutletContext } from 'react-router-dom'
import PricesDisplay from '../../../../components/PricesDisplay'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import FieldsManager, { FieldsManagerType, WhichFieldsToShowType } from '../../../../components/FieldManager'
import { tableFields } from '../../../../components/FieldManager/table-fields'
import { Settings } from 'lucide-react'
import { useTenant } from '../../../../contexts/TenantContext'
import { TableContainer } from '../../Stock/style'

interface PriceItem {
  id: number
  price: string
  unitOfMeasurement: { id: number; name: string }
}

interface ContextType {
  search: string;
}

interface Resource {
  id: number
  name: string
  code?: string
  category: { id: number; name: string } | null
  type: { id: number; name: string } | null
  prices?: PriceItem[]
  description: string
  totalWeight?: number
  lengthInMm?: number
  unitQuantity?: number
  stockUnit?: string
  dimension1?: number | null
  dimension2?: number | null
  material?: string | null
  format?: string | null
  fabrication?: string | null
  ncm?: string | null
  shelf?: string | null
  stockLocation?: string | null
  stockStreet?: string | null
  suppliers: { id: number; companyName: string; cnpj: string }[]
  colors?: Array<{ id: number; name: string }>
  isActivated?: boolean
}

const STORAGE_KEY_STEEL = 'resourceTableFields'
const STORAGE_KEY_FASHION = 'resourceTableFieldsFashion'

const ColorSwatchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ColorSwatchCircle = styled.span<{ $hex?: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => {
    const hex = props.$hex;
    if (!hex) return '#bdbdbd';
    return hex.startsWith('#') ? hex : `#${hex}`;
  }};
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: inline-block;
  flex-shrink: 0;
`

const ResourcesTableWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const getStoredFields = (isFashion: boolean): FieldsManagerType => {
  const key = isFashion ? STORAGE_KEY_FASHION : STORAGE_KEY_STEEL
  const base = (
    tableFields.find(t => t.tableSection === 'resource') ||
    { tableSection: 'resource', whichFieldsToShow: [] }
  ) as FieldsManagerType

  try {
    const raw = localStorage.getItem(key)
    if (raw) {
      const parsed: FieldsManagerType = JSON.parse(raw)
      if (parsed?.whichFieldsToShow?.length) {
        const parsedMap = new Map(parsed.whichFieldsToShow.map(f => [f.field, f]))
        const merged = base.whichFieldsToShow.map(baseField => {
          const storedField = parsedMap.get(baseField.field)
          return storedField
            ? { ...baseField, checked: storedField.checked }
            : baseField
        })
        return {
          ...base,
          whichFieldsToShow: merged,
        }
      }
    }
  } catch { /* */ }

  const filteredFields = isFashion
    ? base.whichFieldsToShow
    : base.whichFieldsToShow.filter(f => f.field !== 'color')

  return {
    ...base,
    whichFieldsToShow: filteredFields.map(f =>
      f.field === 'name' ? { ...f, fieldName: 'Aviamento' } : f
    ),
  }
}

const ResourcesTable: React.FC = () => {
  const { isFashionTenant } = useTenant()
  const storageKey = isFashionTenant ? STORAGE_KEY_FASHION : STORAGE_KEY_STEEL
  const [resources, setResources] = useState<Resource[]>([])
  const [total, setTotal] = useState<number>(0)
  const [skip, setSkip] = useState<number>(0)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [fields, setFields] = useState<FieldsManagerType>(() => getStoredFields(isFashionTenant))
  const columnMenuRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(fields))
  }, [fields, storageKey])

  const navigate = useNavigate()
  const TAKE = 50

  const context = useOutletContext<ContextType>() || { search: '' };
  const { search } = context;

  const [debouncedSearch, setDebouncedSearch] = useState<string>(search)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)

    return () => clearTimeout(handler)
  }, [search])



  useEffect(() => {
    const getResources = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/resources/resource`, {
          params: { search: debouncedSearch, skip: 0, take: TAKE }
        })
        if (response.data) {
          const items = response.data.data ?? response.data
          const totalItems = response.data.total ?? (Array.isArray(response.data) ? response.data.length : 0)

          setResources(items)
          setTotal(totalItems)
          setSkip(TAKE)
        }
      } catch (error) {
        console.error("Erro ao buscar recursos:", error);
      }
    }
    getResources()
  }, [debouncedSearch])

  const handleLoadMore = async () => {
    setIsLoadingMore(true)
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/resources/resource`,
        { params: { search: debouncedSearch, skip, take: TAKE } },
      )
      if (response.data) {
        const newItems: Resource[] = response.data.data ?? response.data
        const totalItems = response.data.total ?? (Array.isArray(response.data) ? response.data.length : 0)

        setResources(prev => [...prev, ...newItems])
        setTotal(totalItems)
        setSkip(prev => prev + TAKE)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingMore(false)
    }
  }

  const getCellValue = (resource: Resource, field: WhichFieldsToShowType): { content: React.ReactNode; title: string } => {
    const sentinel = '—'
    const value = (() => {
      switch (field.field) {
        case 'name': return resource.name
        case 'category': return resource.category?.name || sentinel
        case 'type': return resource.type?.name || sentinel
        case 'code': return resource.code || sentinel
        case 'prices': return <PricesDisplay prices={(resource.prices || []) as any} maxWidth={200} />
        case 'color': {
          const colors = (resource as any).colors || resource.colors || []
          if (Array.isArray(colors) && colors.length > 0) {
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {colors.map((c: any, ci: number) => (
                  <ColorSwatchContainer key={ci}>
                    <ColorSwatchCircle $hex={c.pantone?.hex || c.hex} />
                    <span>
                      {c.name}
                      {c.pantone?.code ? ` (${c.pantone.code})` : ''}
                    </span>
                  </ColorSwatchContainer>
                ))}
              </div>
            )
          }
          return (resource as any).color || sentinel
        }
        case 'suppliers': return resource.suppliers?.map(s => s.companyName).join(', ') || sentinel
        case 'description': return resource.description || sentinel
        case 'totalWeight': return resource.totalWeight != null ? resource.totalWeight : sentinel
        case 'lengthInMm': return resource.lengthInMm != null ? resource.lengthInMm : sentinel
        case 'unitQuantity': return resource.unitQuantity != null ? resource.unitQuantity : sentinel
        case 'stockUnit': return resource.stockUnit || sentinel
        case 'dimension1': return resource.dimension1 != null ? resource.dimension1 : sentinel
        case 'dimension2': return resource.dimension2 != null ? resource.dimension2 : sentinel
        case 'material': return resource.material || sentinel
        case 'format': return resource.format || sentinel
        case 'fabrication': return resource.fabrication || sentinel
        case 'ncm': return resource.ncm || sentinel
        case 'shelf': return resource.shelf || sentinel
        case 'stockLocation': return resource.stockLocation || sentinel
        case 'stockStreet': return resource.stockStreet || sentinel
        default: return sentinel
      }
    })()

    return {
      content: value,
      title: typeof value === 'string' || typeof value === 'number' ? String(value) : '',
    }
  }

  const visibleFields = fields.whichFieldsToShow.filter(f => f.checked && (isFashionTenant || f.field !== 'color'))


  const getFieldLabel = (field: WhichFieldsToShowType): string => {
    if (field.field === 'name') return isFashionTenant ? 'Aviamento' : 'Insumo'
    return field.fieldName
  }

  return (
    <ResourcesTableWrapper>
      <TableContainer>
        <GlobalTable>
          <thead>
            <HeaderLine>
              {visibleFields.map((field, index) => (
                <Th key={index}>{getFieldLabel(field)}</Th>
              ))}
              <Th>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'inline-block' }}>Ações</div>
                  <ConfigMenuWrapper ref={columnMenuRef}>
                    <ConfigMenuButton onClick={() => setIsDropdownOpen(prev => !prev)} title='Mostrar/ocultar colunas' style={{ color: '#bdbdbd' }}>
                      <Settings size={20} />
                    </ConfigMenuButton>
                    {isDropdownOpen && (
                      <FieldsManager
                        dropdownOpen={setIsDropdownOpen}
                        fields={fields}
                        setFields={setFields}
                      />
                    )}
                  </ConfigMenuWrapper>
                </div>
              </Th>
            </HeaderLine>
          </thead>
          <tbody>
            {resources.map((resource, index) => {
              const rowClassName = [resource.isActivated === false ? 'yellowLine' : '']
                .filter(Boolean)
                .join(' ')

              return (
                <Tr
                  key={index}
                  className={rowClassName}
                  style={{ color: resource.isActivated === false ? '#9d7d07' : '#000' }}
                >
                  {visibleFields.map((field, fi) => {
                    const cell = getCellValue(resource, field)
                    return (
                      <Td key={fi} title={cell.title}>
                        {cell.content}
                      </Td>
                    )
                  })}
                  <TdButtons>
                    <img
                      src={lookup}
                      alt='lookup'
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`view?id=${resource.id}`)}
                    />
                  </TdButtons>
                </Tr>
              )
            })}
          </tbody>
        </GlobalTable>
        <LoadMoreButton
          onLoadMore={handleLoadMore}
          hasMore={resources.length < total}
          currentCount={resources.length}
          totalCount={total}
          isLoading={isLoadingMore}
        />
      </TableContainer>
    </ResourcesTableWrapper>
  )
}

export default ResourcesTable
